import React, { useEffect, useState } from "react";
import { Modal } from "newui/components/ui/Modal";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { useCloneRole } from "newui/features/RolesPermissions/hooks/useRoles";
import type { RoleResource } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { showToast } from "utils/helpers";

export interface CloneRoleModalProps {
	isOpen: boolean;
	role: RoleResource | null;
	onClose: () => void;
}

export const CloneRoleModal = ({ isOpen, role, onClose }: CloneRoleModalProps) => {
	const [name, setName] = useState("");
	const cloneRoleMutation = useCloneRole();

	useEffect(() => {
		if (role) {
			setName(`Copy of ${role.name}`);
		}
	}, [role]);

	const handleClose = () => {
		cloneRoleMutation.reset();
		onClose();
	};

	const handleConfirm = () => {
		if (!role || !name.trim()) {
			return;
		}

		cloneRoleMutation.mutate(
			{ source_role_id: role.id, name: name.trim() },
			{
				onSuccess: () => {
					showToast(`"${name.trim()}" created from "${role.name}"`, "Role cloned", true, "clone-role-success");
					handleClose();
				},
				onError: () => {
					showToast("Unable to clone this role. Please try again.", "Clone failed", false, "clone-role-error");
				},
			}
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Clone Role"
			description="Create a new role with a copy of this role's permission matrix."
			iconType="create"
			size="md"
			primaryButton={{
				text: cloneRoleMutation.isPending ? "Cloning..." : "Clone Role",
				btntype: "primary",
				disabled: cloneRoleMutation.isPending || !name.trim(),
				isLoading: cloneRoleMutation.isPending,
				onClick: handleConfirm,
			}}
			secondaryButton={{
				text: "Cancel",
				btntype: "secondary",
				disabled: cloneRoleMutation.isPending,
				onClick: handleClose,
			}}
		>
			<div data-testid="clone-role-modal-content">
				<InputWithLabel
					label="New role name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					testId="clone-role-name-input"
					required
				/>
			</div>
		</Modal>
	);
};
